// src/pages/BeingsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Button,
} from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Client, Pet, clientsAPI, petsAPI } from '../services/api';
import ClientDetailsCard from '../components/cards/ClientDetailsCard';
import PetDetailsCard from '../components/cards/PetDetailsCard';
import ClientPetsCard from '../components/cards/ClientPetsCard';
import ClientBookingsCard from '../components/cards/ClientBookingsCard';
import ClientAccountCard from '../components/cards/ClientAccountCard';
import BookingForm from '../components/forms/BookingForm';
import ClientForm from '../components/forms/ClientForm';
import PetForm from '../components/forms/PetForm';
import { BeingsPageSubMenu } from '../components/menus';
import { useErrorHandler, ErrorDisplayType } from '../errors';
import { PageError } from '../components/common/PageError';
import { getApiErrorMessage } from '../utils/validationHelpers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getCustomIcon } from '../utils/customIcons';
import PageTitle, { PageTitleText } from '../components/common/PageTitle';

type BeingType = 'client' | 'pet';
type PageMode =
  | 'new'
  | 'view'
  | 'edit'
  | 'newpet'
  | 'newbooking'
  | 'bookings'
  | 'pets'
  | 'quotations'
  | 'admin';

// Styled Components
const MainContainer = styled(Box)({
  position: 'relative',
});

const PageContainer = styled(Box)({
  margin: '0 auto',
  maxWidth: '100%',
});

const OwnerLink = styled('span')(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 500,
  color: '#ffffff',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  margin: '0',
  borderRadius: '0',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: 'none !important',
    borderColor: 'transparent !important',
    transform: 'none !important',
  },
  '&:last-child': {
    paddingBottom: 0,
  },
}));

const BeingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { handleError } = useErrorHandler();
  const [error, setError] = useState<string | null>(null);

  const beingType: BeingType = location.pathname.includes('/pets/')
    ? 'pet'
    : 'client';

  const pageMode: PageMode = location.pathname.includes('/newpet')
    ? 'newpet'
    : location.pathname.includes('/newbooking')
      ? 'newbooking'
      : location.pathname.includes('/new')
        ? 'new'
        : location.pathname.includes('/edit')
          ? 'edit'
          : location.pathname.includes('/quotations')
            ? 'quotations'
            : location.pathname.includes('/bookings')
              ? 'bookings'
              : location.pathname.includes('/account')
                ? 'admin'
                : location.pathname.includes('/clients/') &&
                    location.pathname.includes('/pets')
                  ? 'pets'
                  : 'view';

  const [displayClient, setDisplayClient] = useState<Client | null>(null);
  const [displayPet, setDisplayPet] = useState<Pet | null>(null);
  const [clientPets, setClientPets] = useState<Pet[]>([]);
  const [bookingCount, setBookingCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pageMode === 'new') {
      setLoading(false);
    }
  }, [pageMode]);

  // Data fetching
  const fetchData = useCallback(async () => {
    if (!id || pageMode === 'new') return;
    setLoading(true);
    setError(null);
    setBookingCount(null);
    try {
      const numericId = parseInt(id);
      if (isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid ID');
      }

      if (beingType === 'client') {
        const clientData = await clientsAPI.getById(numericId);
        setDisplayClient(clientData);

        if (clientData.pets) {
          setClientPets(clientData.pets);
        } else {
          try {
            const petsData = await petsAPI.getByClient(numericId);
            setClientPets(petsData);
          } catch (petErr) {
            setClientPets([]);
          }
        }
      } else {
        const petData = await petsAPI.getById(numericId);
        setDisplayPet(petData);

        if (petData.clientId) {
          try {
            const clientData = await clientsAPI.getById(petData.clientId);
            setDisplayClient(clientData);
          } catch (clientErr) {
            handleError(clientErr, {
              context: 'loading pet owner data',
              showToast: false,
            });
          }
        }
      }
    } catch (err) {
      const errorMessage = getApiErrorMessage(
        err,
        beingType === 'client' ? 'LOAD_CLIENT' : 'LOAD_PET'
      );
      setError(errorMessage);
      handleError(err, {
        context: `loading ${beingType} data`,
        displayType: ErrorDisplayType.PAGE,
        showToast: false,
      });
    } finally {
      setLoading(false);
    }
  }, [id, beingType, pageMode, handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation handlers
  const handleOwnerClick = useCallback(
    (clientId: number) => {
      navigate(`/clients/${clientId}`);
    },
    [navigate]
  );

  const handlePetView = (petId: number) => {
    navigate(`/pets/${petId}`);
  };

  const handlePetEdit = (petId: number) => {
    navigate(`/pets/${petId}/edit`);
  };

  const handlePetNewBooking = (petId: number) => {
    const pet = clientPets.find((p) => p.id === petId);
    if (pet?.client?.id) {
      navigate(`/clients/${pet.client.id}/newbooking`);
    } else if (displayClient) {
      navigate(`/clients/${displayClient.id}/newbooking`);
    }
  };

  const handlePetViewBookings = (petId: number) => {
    const pet = clientPets.find((p) => p.id === petId);
    if (pet?.client?.id) {
      navigate(`/clients/${pet.client.id}/bookings`);
    } else if (displayClient) {
      navigate(`/clients/${displayClient.id}/bookings`);
    }
  };

  const handlePetViewAdmin = (petId: number) => {
    const pet = clientPets.find((p) => p.id === petId);
    if (pet?.client?.id) {
      navigate(`/clients/${pet.client.id}/account`);
    } else if (displayClient) {
      navigate(`/clients/${displayClient.id}/account`);
    }
  };

  const handlePetViewMedical = (petId: number) => {
    // TODO: Implement medical records navigation
  };

  // Build page header title content
  const getDisplayName = () => {
    if (pageMode === 'new') {
      return `New ${beingType === 'client' ? 'Client' : 'Pet'}`;
    }

    if (pageMode === 'newpet' && displayClient) {
      return (
        <>
          {displayClient.firstName} {displayClient.lastName} / New Pet
        </>
      );
    }

    if (pageMode === 'newbooking') {
      if (beingType === 'pet' && displayPet) {
        return (
          <>
            New Booking for {displayPet.name}
            {displayPet.client && (
              <>
                {' / '}
                <OwnerLink
                  onClick={() => navigate(`/clients/${displayPet.client!.id}`)}
                >
                  {displayPet.client.firstName} {displayPet.client.lastName}
                </OwnerLink>
              </>
            )}
          </>
        );
      }
      if (beingType === 'client' && displayClient) {
        return `${displayClient.firstName} ${displayClient.lastName} / New Booking`;
      }
      return 'New Booking';
    }

    if (displayClient && beingType === 'client' && pageMode === 'pets') {
      return `${displayClient.firstName} ${displayClient.lastName} / Pets (${clientPets.length})`;
    }
    if (displayClient && beingType === 'client' && pageMode === 'bookings') {
      const countStr = bookingCount !== null ? ` (${bookingCount})` : '';
      return `${displayClient.firstName} ${displayClient.lastName} / Bookings${countStr}`;
    }
    if (displayClient && beingType === 'client' && pageMode === 'edit') {
      return `${displayClient.firstName} ${displayClient.lastName} / Edit`;
    }
    if (displayClient && beingType === 'client' && pageMode === 'admin') {
      return `${displayClient.firstName} ${displayClient.lastName} / Account`;
    }

    if (displayPet && beingType === 'pet' && pageMode === 'edit') {
      const client = displayPet.client ?? displayClient;
      return (
        <>
          {displayPet.name} / Edit
          {client && (
            <>
              {' / '}
              <OwnerLink onClick={() => navigate(`/clients/${client.id}`)}>
                {client.firstName} {client.lastName}
              </OwnerLink>
            </>
          )}
        </>
      );
    }

    if (displayPet && beingType === 'pet' && pageMode === 'admin') {
      return `${displayPet.name} / Account`;
    }

    if (displayClient && beingType === 'client') {
      return `${displayClient.firstName} ${displayClient.lastName}`;
    }
    if (displayPet && beingType === 'pet') {
      return displayPet.name;
    }

    return beingType === 'client' ? 'Client' : 'Pet';
  };

  // Build the full title ReactNode (name + optional pet owner link for view mode)
  const getHeaderTitle = () => {
    const displayName = getDisplayName();
    const showPetOwnerLink =
      beingType === 'pet' &&
      displayPet &&
      pageMode === 'view' &&
      displayPet.client;

    return (
      // See PageTitle.tsx's own comment on this exact pattern: role +
      // aria-level applied here at the usage site, matching DialogBase's
      // DialogTitleText fix, since FormPageTitle can't safely carry this
      // as a defaultProp under React 19.
      <PageTitleText role="heading" aria-level={1}>
        {displayName}
        {showPetOwnerLink && (
          <>
            <span style={{ fontWeight: 500, margin: '0 10px' }}>/</span>
            <OwnerLink
              onClick={() => navigate(`/clients/${displayPet.client!.id}`)}
            >
              {displayPet.client!.firstName} {displayPet.client!.lastName}
            </OwnerLink>
          </>
        )}
      </PageTitleText>
    );
  };

  // Build left-side icons for the page header
  const getHeaderLeftContent = () => {
    const hasWarning =
      (beingType === 'client' && displayClient?.showWarning) ||
      (beingType === 'pet' && displayPet?.showWarning);
    const showCross = beingType === 'pet' && !displayPet?.stillOwned;

    // Type icon, matching ClientsList/PetsList's own per-row icons.
    const typeIcon =
      beingType === 'client' ? (
        <PersonIcon sx={{ fontSize: '2.25rem', color: '#fff' }} />
      ) : (
        getCustomIcon(displayPet?.petType?.type, '2rem', '#ffffff')
      );

    return (
      <>
        {hasWarning && (
          <Box
            sx={{
              padding: showCross ? '0 0 0 0.5rem' : '0 0.5rem',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <WarningIcon
              sx={{
                fontSize: '2.25rem',
                color: '#fff',
                marginLeft: '0.5rem',
                '@media (min-width: 600px)': {
                  marginLeft: '2rem',
                },
              }}
            />
          </Box>
        )}
        {showCross && (
          <Box
            sx={{
              paddingRight: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {getCustomIcon('cross', '2.4rem', '#ffffff')}
          </Box>
        )}
        {!hasWarning && (
          <Box
            sx={{
              width: '1rem',
              '@media (min-width: 600px)': {
                width: '3rem',
              },
            }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            paddingRight: '0.5rem',
          }}
        >
          {typeIcon}
        </Box>
      </>
    );
  };

  const shouldShowSubMenu = () => {
    if (pageMode === 'new') return false;
    if (beingType === 'client' && displayClient) return true;
    if (beingType === 'pet' && (displayPet || displayClient)) return true;
    return false;
  };

  const getSubMenuBeing = () => {
    if (beingType === 'client' && displayClient) {
      return displayClient;
    } else if (beingType === 'pet' && displayPet) {
      return displayPet;
    } else if (pageMode === 'newbooking' && displayClient) {
      return displayClient;
    }
    return null;
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner message={`Loading ${beingType} details...`} size={60} />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageError message={error} onRetry={fetchData} />
      </PageContainer>
    );
  }

  return (
    <MainContainer>
      <PageTitle
        title={getHeaderTitle()}
        showSeparator={false}
        leftContent={getHeaderLeftContent()}
      />
      <PageContainer>
        <ContentCard sx={{ p: 0, border: 'none' }}>
          <CardContent
            sx={{
              p: 0,
              '&:last-child': {
                paddingBottom: 0,
              },
            }}
          >
            {shouldShowSubMenu() && getSubMenuBeing() && (
              <BeingsPageSubMenu
                being={getSubMenuBeing()!}
                type={
                  beingType === 'pet' &&
                  pageMode === 'newbooking' &&
                  displayClient
                    ? 'client'
                    : beingType
                }
                bottomBorder={!location.pathname.includes('/account')}
                buttonBottomBorder={location.pathname.includes('/account')}
                showBackButton={false}
              />
            )}

            {pageMode === 'new' && (
              <Box sx={{ p: '0' }}>
                {beingType === 'client' ? (
                  <ClientForm
                    onSuccess={(client) => {
                      navigate(`/clients/${client.id}/newpet`);
                    }}
                    onCancel={() => navigate('/clients')}
                  />
                ) : (
                  <Box sx={{ p: '2rem', textAlign: 'center' }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Pets can only be created from their owner's profile
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        To create a new pet:
                        <br />
                        1. Find or create the owner's client profile
                        <br />
                        2. Click "NEW PET" from the client's page
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/clients')}
                        sx={{ mr: 2 }}
                      >
                        Browse Clients
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/pets')}
                      >
                        Back to Pets
                      </Button>
                    </Alert>
                  </Box>
                )}
              </Box>
            )}

            {pageMode === 'edit' && (
              <Box sx={{ p: '0' }}>
                {beingType === 'client' && displayClient ? (
                  <ClientForm
                    client={displayClient}
                    onSuccess={(updatedClient) => {
                      setDisplayClient(updatedClient);
                      navigate(`/clients/${updatedClient.id}`);
                    }}
                    onCancel={() => navigate(`/clients/${id}`)}
                  />
                ) : beingType === 'pet' && displayPet ? (
                  displayPet.client ? (
                    <PetForm
                      client={displayPet.client}
                      pet={displayPet}
                      onSuccess={(result) => {
                        navigate(`/pets/${result.petId}`);
                      }}
                      onCancel={() => navigate(`/pets/${id}`)}
                    />
                  ) : (
                    <Box sx={{ p: '2rem', textAlign: 'center' }}>
                      <Alert severity="error">
                        Unable to load pet owner information. Please try
                        refreshing the page.
                      </Alert>
                      <Button
                        variant="outlined"
                        onClick={() => window.location.reload()}
                        sx={{ mt: 2 }}
                      >
                        Refresh Page
                      </Button>
                    </Box>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      p: '2rem',
                    }}
                  >
                    <LoadingSpinner message="Loading..." size={60} />
                  </Box>
                )}
              </Box>
            )}

            {pageMode === 'newpet' && (
              <Box sx={{ p: '0' }}>
                {displayClient ? (
                  <PetForm
                    client={displayClient}
                    onSuccess={(result) => {
                      if (result.isEditMode) {
                        navigate(`/pets/${result.petId}`);
                      } else {
                        navigate(`/clients/${displayClient.id}/pets`);
                      }
                    }}
                    onCancel={() => navigate(`/clients/${displayClient.id}`)}
                  />
                ) : loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      p: '2rem',
                    }}
                  >
                    <LoadingSpinner message="Loading client..." size={60} />
                  </Box>
                ) : (
                  <Box sx={{ p: '2rem', textAlign: 'center' }}>
                    <Alert severity="error">
                      Unable to load client information for pet creation.
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/clients')}
                      sx={{ mt: 2 }}
                    >
                      Back to Clients
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {pageMode === 'newbooking' && (
              <Box sx={{ p: '0' }}>
                {beingType === 'pet' && displayPet && displayPet.client ? (
                  <BookingForm
                    client={displayPet.client}
                    onSuccess={() => {
                      navigate(`/pets/${displayPet.id}`);
                    }}
                    onCancel={() => navigate(`/pets/${displayPet.id}`)}
                  />
                ) : beingType === 'client' && displayClient ? (
                  <BookingForm
                    client={{ ...displayClient, pets: clientPets }}
                    onSuccess={() => {
                      navigate(`/clients/${displayClient.id}`);
                    }}
                    onCancel={() => navigate(`/clients/${displayClient.id}`)}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      p: '2rem',
                    }}
                  >
                    <LoadingSpinner message="Loading..." size={60} />
                  </Box>
                )}
              </Box>
            )}

            {pageMode === 'view' && beingType === 'client' && displayClient && (
              <ClientDetailsCard client={displayClient} />
            )}

            {pageMode === 'view' && beingType === 'pet' && displayPet && (
              <PetDetailsCard
                pet={displayPet}
                onOwnerClick={handleOwnerClick}
              />
            )}

            {pageMode === 'pets' && beingType === 'client' && displayClient && (
              <ClientPetsCard
                pets={clientPets}
                client={displayClient}
                onEdit={handlePetEdit}
                onView={handlePetView}
                onNewBooking={handlePetNewBooking}
                onViewBookings={handlePetViewBookings}
                onViewAdmin={handlePetViewAdmin}
                onViewMedical={handlePetViewMedical}
                onOwnerClick={handleOwnerClick}
              />
            )}

            {pageMode === 'bookings' &&
              beingType === 'client' &&
              displayClient && (
                <ClientBookingsCard
                  client={displayClient}
                  clientId={displayClient.id}
                  onCountReady={setBookingCount}
                />
              )}

            {pageMode === 'quotations' &&
              beingType === 'client' &&
              displayClient && (
                <ClientBookingsCard
                  client={displayClient}
                  clientId={displayClient.id}
                  mode="quotations"
                  onCountReady={setBookingCount}
                />
              )}

            {pageMode === 'quotations' && beingType === 'pet' && displayPet && (
              <ClientBookingsCard
                clientId={displayPet.clientId}
                mode="quotations"
                onCountReady={setBookingCount}
              />
            )}

            {pageMode === 'bookings' && beingType === 'pet' && displayPet && (
              <ClientBookingsCard
                clientId={displayPet.clientId}
                onCountReady={setBookingCount}
              />
            )}

            {pageMode === 'admin' &&
              beingType === 'client' &&
              displayClient && <ClientAccountCard client={displayClient} />}
          </CardContent>
        </ContentCard>
      </PageContainer>
    </MainContainer>
  );
};

export default BeingsPage;
